/**
 * Quick Add FAB (Floating Action Button)
 *
 * Mobile-only button for quickly adding tasks. Sits above the tab bar.
 * Opens a bottom sheet with a text input for task creation.
 * Also offers a Brain Dump shortcut to open the voice-to-tasks flow.
 *
 * The input supports the quick-add token grammar (see
 * reqts/quick-add-token-grammar.md): #project @label p1-p4 ~estimate
 * *recurrence, natural dates, and a // description tail — with inline
 * autocomplete for the enumerable tokens. Tokens are parsed out of the title
 * on submit.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Plus, Loader2, Mic, Sparkles, Info } from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { createTask } from '../api/client';
import { createProject } from '../api/projects';
import { useProjects } from '../hooks/useProjects';
import { useLabels } from '../hooks/useLabels';
import {
  parseQuickAdd,
  getQuickAddSuggestions,
  applySuggestion,
  type ActiveSuggestions,
} from '../lib/quickAddParser';
import { BrainDumpOverlay } from './BrainDumpOverlay';

interface QuickAddFabProps {
  onTaskCreated?: () => void;
}

// Shown in the (i) help popover — the quick-add token grammar
// (see reqts/quick-add-token-grammar.md).
const QUICK_ADD_SHORTCUTS: { token: string; desc: string }[] = [
  { token: '#project', desc: 'File under a project' },
  { token: '@label', desc: 'Add a label' },
  { token: 'p1–p4', desc: 'Priority (p1 = urgent)' },
  { token: '~30m ~1h', desc: 'Time estimate' },
  { token: '*weekly', desc: 'Repeat (daily · weekday · weekly · monthly)' },
  { token: 'tomorrow 2pm', desc: 'Due date / time' },
  { token: '// note', desc: 'Add a description' },
];

export function QuickAddFab({ onTaskCreated }: QuickAddFabProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isBrainDumpOpen, setIsBrainDumpOpen] = useState(false);
  const [taskContent, setTaskContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState<ActiveSuggestions | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { projects, refetch: refetchProjects } = useProjects();
  const { labelNames } = useLabels();

  // Recompute the active-token suggestions for the caret position.
  const refreshSuggestions = useCallback(
    (value: string, caret: number) => {
      setSuggestions(getQuickAddSuggestions(value, caret, { projects, labels: labelNames }));
      setHighlight(0);
    },
    [projects, labelNames],
  );

  const acceptSuggestion = (token: string) => {
    if (!suggestions) return;
    const { text, caret } = applySuggestion(taskContent, suggestions, token);
    setTaskContent(text);
    setSuggestions(null);
    // Restore focus + caret after the value commits.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaskContent(e.target.value);
    refreshSuggestions(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions || suggestions.items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.items.length) % suggestions.items.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      // Accept the highlighted suggestion instead of submitting the form.
      e.preventDefault();
      acceptSuggestion(suggestions.items[highlight]!.token);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setSuggestions(null);
    }
  };

  // Lock body scroll while sheet is open. NOTE: focus is handled synchronously
  // in the FAB tap handler (see below), NOT here — a deferred/effect focus
  // breaks iOS Safari's user-activation chain and the keyboard won't appear.
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setTaskContent('');
    setSuggestions(null);
    setShowHelp(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const parsed = parseQuickAdd(taskContent, { projects, labels: labelNames });
    if (!parsed.content) return;

    setIsSubmitting(true);
    try {
      let projectId = parsed.projectId;
      if (parsed.newProjectName) {
        const project = await createProject({ name: parsed.newProjectName });
        projectId = project.id;
        void refetchProjects();
      }

      // Time estimate is stored as a label bucket (matches TaskCreateForm).
      const allLabels = parsed.timeEstimate
        ? [...parsed.labels, parsed.timeEstimate]
        : parsed.labels;

      await createTask({
        content: parsed.content,
        description: parsed.description,
        projectId,
        priority: parsed.priority,
        dueDate: parsed.dueDate,
        dueTime: parsed.dueTime,
        recurrenceRule: parsed.recurrenceRule,
        labels: allLabels.length > 0 ? allLabels : undefined,
      });

      setTaskContent('');
      setSuggestions(null);
      setIsOpen(false);
      onTaskCreated?.();
      toast.success('Task added');
    } catch (error) {
      // Surface the failure (the old code swallowed it) so the user isn't left
      // wondering whether the task saved.
      toast.error(error instanceof Error ? error.message : 'Failed to add task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBrainDumpClose = () => {
    setIsBrainDumpOpen(false);
  };

  const handleBrainDumpTasksCreated = () => {
    onTaskCreated?.();
  };

  return (
    <>
      {/* Brain Dump full-screen overlay */}
      {isBrainDumpOpen && (
        <BrainDumpOverlay
          onClose={handleBrainDumpClose}
          onTasksCreated={handleBrainDumpTasksCreated}
        />
      )}

      {/* FAB button */}
      <button
        onClick={() => {
          // Mount the sheet synchronously (flushSync) then focus the input
          // within this same tap gesture, so iOS Safari raises the keyboard
          // immediately instead of requiring a second tap on the input.
          flushSync(() => setIsOpen(true));
          inputRef.current?.focus();
        }}
        className="md:hidden fixed right-4 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 shadow-lg flex items-center justify-center active:scale-95 transition-transform"
        // Reclaim ~1rem from the home-indicator inset so the FAB sits close to
        // the bottom instead of floating half a button-height above it. The
        // device reports a larger safe-area-inset-bottom than the button needs
        // to clear; max() keeps a small floor on desktop (inset = 0).
        style={{ bottom: 'max(0.25rem, calc(env(safe-area-inset-bottom, 0px) - 1rem))' }}
        aria-label="Add task"
      >
        <Plus className="w-7 h-7 text-surface-base-dark" />
      </button>

      {/* Bottom sheet overlay — uses height:100dvh to avoid iOS keyboard push */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 z-50" style={{ height: '100dvh' }}>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={handleClose}
          />
          {/* Sheet */}
          <div className="absolute bottom-0 inset-x-0 bg-surface-raised-light dark:bg-surface-raised-dark rounded-t-2xl p-4 pb-safe">
            {/* Drag handle */}
            <div className="flex justify-center mb-3">
              <div className="w-10 h-1 rounded-full bg-border-subtle-light dark:bg-border-subtle-dark" />
            </div>

            {/* Brain Dump button */}
            <button
              onClick={() => {
                handleClose();
                setIsBrainDumpOpen(true);
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 mb-3 rounded-xl border border-dashed border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            >
              <div className="flex items-center gap-1.5">
                <Mic className="w-4 h-4" />
                <Sparkles className="w-3.5 h-3.5" />
              </div>
              <span className="text-sm font-medium">Brain Dump — speak multiple tasks at once</span>
            </button>

            <form onSubmit={handleSubmit}>
              {/* Autocomplete dropdown — sits directly above the input */}
              {suggestions && suggestions.items.length > 0 && (
                <ul
                  role="listbox"
                  className="mb-2 max-h-48 overflow-y-auto rounded-xl border border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light dark:bg-surface-base-dark shadow-lg"
                >
                  {suggestions.items.map((item, idx) => (
                    <li key={`${item.kind}:${item.token}`} role="option" aria-selected={idx === highlight}>
                      <button
                        type="button"
                        // onMouseDown (not onClick) so the input doesn't blur before we apply.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          acceptSuggestion(item.token);
                        }}
                        className={clsx(
                          'w-full text-left px-4 py-2 text-sm font-mono flex items-center gap-2',
                          idx === highlight
                            ? 'bg-primary-500/15 text-primary-700 dark:text-primary-300'
                            : 'text-text-primary-light dark:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark',
                        )}
                      >
                        <span className="text-text-tertiary-light dark:text-text-tertiary-dark shrink-0">{item.token}</span>
                        <span className="truncate">{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Shortcuts help — toggled by the (i) in the input corner */}
              {showHelp && (
                <div
                  role="dialog"
                  aria-label="Quick-add shortcuts"
                  className="mb-2 rounded-xl border border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light dark:bg-surface-base-dark shadow-lg p-3"
                >
                  <div className="text-xs font-semibold mb-2 text-text-secondary-light dark:text-text-secondary-dark">
                    Type these in the task to set fields
                  </div>
                  <ul className="space-y-1.5">
                    {QUICK_ADD_SHORTCUTS.map((s) => (
                      <li key={s.token} className="flex items-baseline gap-2 text-xs">
                        <span className="shrink-0 font-mono text-primary-600 dark:text-primary-400">{s.token}</span>
                        <span className="text-text-secondary-light dark:text-text-secondary-dark">{s.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="relative">
                <input
                  ref={inputRef}
                  value={taskContent}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onKeyUp={(e) =>
                    refreshSuggestions(
                      e.currentTarget.value,
                      e.currentTarget.selectionStart ?? e.currentTarget.value.length,
                    )
                  }
                  onClick={(e) =>
                    refreshSuggestions(
                      e.currentTarget.value,
                      e.currentTarget.selectionStart ?? e.currentTarget.value.length,
                    )
                  }
                  placeholder="What needs to be done?  #project @label p1 ~1h"
                  className={clsx(
                    'w-full text-base font-mono bg-surface-base-light dark:bg-surface-base-dark rounded-xl pl-4 pr-10 py-3',
                    'border border-border-subtle-light dark:border-border-subtle-dark',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                    'text-text-primary-light dark:text-text-primary-dark',
                    'placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark'
                  )}
                />
                <button
                  type="button"
                  aria-label="Quick-add shortcuts"
                  aria-expanded={showHelp}
                  // onMouseDown+preventDefault so tapping the icon doesn't blur the input
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setShowHelp((v) => !v);
                    setSuggestions(null);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
              <div className="flex justify-end mt-3 gap-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-mono text-text-secondary-light dark:text-text-secondary-dark rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!taskContent.trim() || isSubmitting}
                  className={clsx(
                    'px-4 py-2 text-sm font-mono font-medium rounded-lg transition-colors',
                    taskContent.trim() && !isSubmitting
                      ? 'bg-primary-500 text-surface-base-dark hover:bg-primary-600'
                      : 'bg-primary-500/40 text-surface-base-dark/60 cursor-not-allowed'
                  )}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Add Task'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
